// w11 Magnum Atlas — royal Bukhara wedding preview card.
//
// The catalog card uses the "outer frame + inner card" structure styled
// as a Bukhara palace pavilion: a saffron-gold suzani foil gradient
// frame surrounds the deep-aubergine inner card. The focal element is
// a circular photo medallion behind a single large chrysanthemum-roundel
// suzani motif, flanked by paisleys at the corners. Names in Cinzel
// italic gold, caption rendered as a small-caps banner.
import { Photo, type LayoutCtx } from "./_shared";

const SUZANI_FOIL =
  "linear-gradient(135deg, #6E5316 0%, #C6A75A 22%, #F1D77A 45%, #C6A75A 65%, #6E5316 100%)";

const CREAM = "#F4E5C2";

function ChrysanthemumRoundel({
  size,
  accent,
  ink,
}: {
  size: number;
  accent: string;
  ink: string;
}) {
  const petals = Array.from({ length: 10 }).map((_, i) => {
    const a = (i * 36 * Math.PI) / 180;
    const cx = 50 + Math.cos(a) * 24;
    const cy = 50 + Math.sin(a) * 24;
    return (
      <ellipse
        key={i}
        cx={cx}
        cy={cy}
        rx="9"
        ry="5"
        fill="none"
        stroke={accent}
        strokeWidth="1.1"
        transform={`rotate(${i * 36} ${cx} ${cy})`}
      />
    );
  });
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
      <circle cx="50" cy="50" r="46" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.8" />
      <circle cx="50" cy="50" r="42" fill="none" stroke={ink} strokeWidth="0.4" opacity="0.55" />
      {petals}
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * 45 * Math.PI) / 180;
        const x2 = 50 + Math.cos(a) * 14;
        const y2 = 50 + Math.sin(a) * 14;
        return <line key={i} x1="50" y1="50" x2={x2} y2={y2} stroke={accent} strokeWidth="0.9" />;
      })}
      <circle cx="50" cy="50" r="6" fill={accent} />
      <circle cx="50" cy="50" r="2.6" fill={ink} />
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * 22.5 * Math.PI) / 180;
        const cx = 50 + Math.cos(a) * 36;
        const cy = 50 + Math.sin(a) * 36;
        return <circle key={i} cx={cx} cy={cy} r="0.9" fill={ink} opacity="0.7" />;
      })}
    </svg>
  );
}

function Paisley({ size, accent }: { size: number; accent: string }) {
  return (
    <svg viewBox="0 0 60 60" width={size} height={size} aria-hidden>
      <path
        d="M 12 50 C 6 30 18 8 38 8 C 50 8 56 18 50 30 C 46 38 36 40 30 36 C 26 34 26 28 30 26 C 34 24 38 28 36 32"
        fill="none"
        stroke={accent}
        strokeWidth="1.1"
      />
      <circle cx="42" cy="20" r="1.4" fill={accent} />
    </svg>
  );
}

export function BukharaRoyalLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: SUZANI_FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 4px ${bg}, inset 0 0 0 4.5px ${accent}88`,
          overflow: "hidden",
        }}
      >
        {/* Paisley corner ornaments */}
        <div style={{ position: "absolute", top: 8, left: 8, opacity: 0.65 }} aria-hidden>
          <Paisley size={24} accent={accent} />
        </div>
        <div style={{ position: "absolute", top: 8, right: 8, opacity: 0.65, transform: "scaleX(-1)" }} aria-hidden>
          <Paisley size={24} accent={accent} />
        </div>
        <div style={{ position: "absolute", bottom: 8, left: 8, opacity: 0.65, transform: "scaleY(-1)" }} aria-hidden>
          <Paisley size={24} accent={accent} />
        </div>
        <div style={{ position: "absolute", bottom: 8, right: 8, opacity: 0.65, transform: "scale(-1, -1)" }} aria-hidden>
          <Paisley size={24} accent={accent} />
        </div>

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.45em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          NIKOH TO'YI · QADRDON
        </div>

        {/* Photo medallion + chrysanthemum overlay */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Suzani roundel behind */}
          <div style={{ position: "absolute", inset: "-10%", display: "grid", placeItems: "center", opacity: 0.55 }} aria-hidden>
            <ChrysanthemumRoundel size={180} accent={accent} ink={CREAM} />
          </div>

          {/* Photo ring */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: SUZANI_FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${accent}55, ${bg})`,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <ChrysanthemumRoundel size={72} accent={accent} ink={ink} />
                    </div>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Double-rule above */}
        <div style={{ width: "60%", height: 3, borderTop: `0.5px solid ${accent}`, borderBottom: `0.5px solid ${accent}aa`, marginBottom: 8 }} />

        {/* Names — Cinzel saffron */}
        <div
          style={{
            fontFamily: font,
            fontSize: 22,
            fontStyle: "italic",
            color: accent,
            letterSpacing: "0.02em",
            lineHeight: 1.05,
          }}
        >
          {names}
        </div>

        {/* Double-rule below */}
        <div style={{ width: "60%", height: 3, borderTop: `0.5px solid ${accent}aa`, borderBottom: `0.5px solid ${accent}`, marginTop: 8 }} />

        <div style={{ flex: 1 }} />

        {/* Caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          NIKOH · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}aa`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${day} ${months[month - 1] ?? ""} ${year}`;
}
